import { SubscriptionOrder } from "../interfaces/subscriptionOrder";
import { fetchWithAuth } from "./fetchWithAuth";

const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

export const getSubscriptionOrderByOrderID = async (
  orderID: number,
): Promise<SubscriptionOrder> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/SubscriptionOrder/GetSubscriptionOrderByOrderID/${orderID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  // If the backend returns 404 Not Found, send back a blank default object
  if (response.status === 404) {
    return {
      subscriptionOrderID: 0,
      orderID,
      adminReviewed: false,
      inAdminReview: false,
    };
  }

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Fetch Subscription Order by OrderID Error:", errorText);
    throw new Error("Failed to fetch subscription order.");
  }

  const subscriptionOrder: SubscriptionOrder = await response.json();
  return subscriptionOrder;
};

export const getSubscriptionOrderByID = async (
  subscriptionOrderID: number,
): Promise<SubscriptionOrder> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/SubscriptionOrder/GetSubscriptionOrderByID/${subscriptionOrderID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(
        `Subscription Order ID ${subscriptionOrderID} was not found.`,
      );
    }

    const errorText = await response.text();
    console.error("Fetch Subscription Order by ID Error:", errorText);
    throw new Error("Failed to fetch subscription order.");
  }

  const order: SubscriptionOrder = await response.json();
  return order;
};

export const updateSubscriptionOrder = async (
  subscriptionOrder: SubscriptionOrder,
): Promise<SubscriptionOrder> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/SubscriptionOrder/UpdateSubscriptionOrder`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(subscriptionOrder),
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Subscription Order was not found to update.`);
    }

    const errorText = await response.text();
    console.error("Update Subscription Order Error:", errorText);
    throw new Error("Failed to update the subscription order.");
  }

  const updatedOrder: SubscriptionOrder = await response.json();
  return updatedOrder;
};

export const checkForOpenSubOrder = async (
  orderID: number,
): Promise<boolean> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/SubscriptionOrder/CheckForOpenSubOrder/${orderID}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error("Failed to check for open sub order.");
    }

    return response.json();
  } catch (error) {
    throw error;
  }
};

export const deleteSubscriptionOrder = async (
  subscriptionOrderID: number,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/SubscriptionOrder/DeleteSubscriptionOrder/${subscriptionOrderID}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(
        `Subscription Order ID ${subscriptionOrderID} was not found.`,
      );
    }

    const errorText = await response.text();
    console.error("Delete Subscription Detail Error:", errorText);
    throw new Error(
      `Failed to delete subscription detail with ID ${subscriptionOrderID}.`,
    );
  }
};

export const createSubscriptionOrder = async (
  orderID: number,
): Promise<SubscriptionOrder> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/SubscriptionOrder/CreateSubscriptionOrder/${orderID}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Create Subscription Order Error:", errorText);
    throw new Error("Failed to create the subscription order.");
  }

  const createdOrder: SubscriptionOrder = await response.json();
  return createdOrder;
};
